import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { isFalabellaEnabledCountry } from '@/constants/falabella';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { setFalabellaAccess } from '@/redux/parcel/parcelSlice';
import { extractApiErrorMessage } from '@/utils/httpUtils';

import type { FalabellaConnectorFormValues } from './falabellaConnectorSchema';
import type { AppDispatch, RootState } from '@/redux/store';

import { falabellaConnectorSchema } from './falabellaConnectorSchema';

interface UseFalabellaConnectorProps {
  clientId: string;
  onConnect: () => void;
  onClose: () => void;
}

const useFalabellaConnector = (props: UseFalabellaConnectorProps) => {
  const { clientId, onConnect, onClose } = props;
  const { t } = useTranslation('falabella');
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.parcel.profile.data);

  const country = profile?.country;
  const isChile = isFalabellaEnabledCountry(country);

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<FalabellaConnectorFormValues>({
    resolver: yupResolver(falabellaConnectorSchema),
    defaultValues: {
      sellerId: '',
      userId: '',
      apiKey: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = handleFormSubmit(async (data: FalabellaConnectorFormValues) => {
    if (!country || !isChile) {
      toast.error(t('connector.onlyChileError'));
      return;
    }

    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.falabella.install({
        clientId,
        sellerId: data.sellerId.trim(),
        userId: data.userId.trim(),
        apiKey: data.apiKey.trim(),
      });

      if (
        !response ||
        ('success' in response && !response.success) ||
        ('code' in response && typeof response.code === 'number' && response.code >= 400)
      ) {
        throw new Error(extractApiErrorMessage(response, t('connector.connectError')));
      }

      toast.success(t('connector.connectSuccess'));
      dispatch(setFalabellaAccess(true));
      onConnect();
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('connector.connectError');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  });

  return {
    loading,
    isChile,
    errors,
    register,
    handleSubmit: onSubmit,
  };
};

export default useFalabellaConnector;
